import React, { useState, useContext, useMemo, useCallback, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  useTheme,
  useMediaQuery,
  Avatar,
  Chip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  // AccountCircle, // substituído por Avatar com foto/iniciais
  Logout,
  Person,
  Brightness4,
  Brightness7,
  Search,
  HelpOutline,
  AutoAwesome,
  PsychologyOutlined,
  ReceiptLongOutlined,
  AdminPanelSettings,
  Storefront,
  AssignmentOutlined,
  SpaceDashboardOutlined,
  AccessTimeOutlined,
} from "@mui/icons-material";

// import logo from "../assets/logo.png";
// import logowhite from "../assets/logowhite.png";
import { useBrand } from "../context/BrandContext";

import CompanySelector from "./CompanySelector";
import SearchAutocomplete from "./SearchAutocomplete";
import Tour from "./Tour"; // Componente de tour
import { useThemeContext } from "../context/ThemeContext";
import { useTranslatedThemeOptions } from "../hooks/useTranslatedThemeOptions";
import { AuthContext } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import {
  buildShellTourBundles,
  mergeShellAndPageSteps,
  filterStepsByExistingTargets,
  resolveTourRouteKey,
  getPageTourSteps,
} from "../tour/platformTour";
import { useTranslation } from "react-i18next";
import NotificationBell from "./notifications/NotificationBell";
import HelpOnboardingDialog from "./HelpOnboardingDialog";
import { useEffectivePermissions, scopeSetFromPermissions, can } from "../hooks/useEffectivePermissions";
import { useWorkspaceConfig } from "../context/WorkspaceConfigContext";
import { useWorkspaceSegment } from "../hooks/useWorkspaceSegment";
import { useSetupOnboardingStatus } from "../hooks/useSetupOnboardingStatus";
import { getSessionRoleIndicator } from "../utils/sessionRoleIndicator";
import { getActiveWorkspaceSlug, readWorkspaceSlugFromUrl } from "../utils/workspaceStorage";
import { PARTNER_CONSOLE_PATH } from "../pages/Partner/PartnerConsole/partnerConsoleRoutes";
import {
  isPlatformConsolePath,
  PLATFORM_CONSOLE_PATH,
  PLATFORM_CONSOLE_SEGMENT,
} from "../pages/Platform/XprobaseConsole/platformConsoleRoutes";
import { usePlatformConsoleAcl } from "../pages/Platform/XprobaseConsole/PlatformConsoleAclContext";
import {
  hasActiveTenantWorkspaceContext,
  isPartnerAssistantContext,
  isPartnerConsolePath,
} from "../utils/partnerPortal";
import { usePlatformAiOptional } from "../context/PlatformAiContext";
import { openTeamPopupWindow } from "../utils/openTeamPopupWindow";
import { openTeamTasksPopupWindow } from "../utils/openTeamTasksPopupWindow";
import SessionRoleWorkspaceSwitcher from "./SessionRoleWorkspaceSwitcher";

/* ================= helpers de avatar ================= */
function stringToColor(string) {
  let hash = 0;
  for (let i = 0; i < String(string).length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = String(string).charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i += 1) {
    // eslint-disable-next-line no-bitwise
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
}

function getInitials(fullname = "") {
  const s = String(fullname).trim();
  if (!s) return "US";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getUserAvatarUrl(user) {
  const u = user?.imagem;
  return typeof u === "string" && u.trim().length > 0 ? u : null;
}
/* ===================================================== */

const TopBar = ({ collapsed, setCollapsed, toggleDrawer, hideWorkspaceChrome = false }) => {
  const brand = useBrand();
  const { t } = useTranslation("common");

  const { logout, userData } = useContext(AuthContext);
  const { companyKey } = useWorkspaceConfig();
  const { segment } = useWorkspaceSegment();
  const { data: setupStatus } = useSetupOnboardingStatus(companyKey, Boolean(companyKey));
  const isModelWorkspace = String(setupStatus?.workspaceKind || "").toLowerCase() === "model";
  const navigate = useNavigate();
  const location = useLocation();
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [modalOpen, setModalOpen] = useState(false); // Modal para busca e seleção de empresa no mobile
  const [helpOnboardingOpen, setHelpOnboardingOpen] = useState(false);
  const [isTourActive, setIsTourActive] = useState(false); // Ativação do tour
  const [tourSteps, setTourSteps] = useState([]);
  const [tourReady, setTourReady] = useState(false);
  const platformAi = usePlatformAiOptional();
  const { changeTheme } = useThemeContext();
  const themeMenuOptions = useTranslatedThemeOptions();
  const theme = useTheme();

  const [themeAnchorEl, setThemeAnchorEl] = useState(null);
  const isThemeMenuOpen = Boolean(themeAnchorEl);

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const sessionRole = useMemo(() => getSessionRoleIndicator(userData), [userData]);

  const openHelpOnboarding = useCallback(() => {
    setHelpOnboardingOpen(true);
  }, []);

  useEffect(() => {
    if (!isTourActive) {
      setTourSteps([]);
      setTourReady(false);
      return;
    }

    setTourReady(false);
    const apply = () => {
      const routeKey = resolveTourRouteKey(location.pathname);
      const pageRaw = getPageTourSteps(routeKey, location.pathname, isMobile, segment);
      const page = filterStepsByExistingTargets(pageRaw);
      const omitMainIntro = page.some((s) => s.target === ".tour-main-content");
      const bundles = buildShellTourBundles(isMobile, { omitMainIntro, segment });
      setTourSteps(mergeShellAndPageSteps(bundles, page));
      setTourReady(true);
    };

    apply();
    const t = setTimeout(apply, 350);
    return () => clearTimeout(t);
  }, [isTourActive, isMobile, location.pathname, segment]);

  const handleThemeMenuOpen = (event) => setThemeAnchorEl(event.currentTarget);
  const handleThemeMenuClose = () => setThemeAnchorEl(null);
  const handleThemeChange = (t) => {
    changeTheme(t);
    setThemeAnchorEl(null);
  };

  const handleProfileMenuOpen = (event) => setProfileAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setProfileAnchorEl(null);

  const isProfileMenuOpen = Boolean(profileAnchorEl);

  const avatarRoleBorder = sessionRole ? `3px solid ${sessionRole.accent}` : `2px solid ${theme.palette.divider}`;

  // Props do Avatar (foto ou iniciais)
  const avatarComputed = useMemo(() => {
    const name = userData?.nome || userData?.name || userData?.email || "Usuário";
    const url = getUserAvatarUrl(userData);
    if (url) {
      return {
        src: url,
        alt: name,
        sx: {
          width: 36,
          height: 36,
          border: avatarRoleBorder,
          boxSizing: "border-box",
        },
        title: name,
      };
    }
    const bg = stringToColor(name);
    return {
      children: getInitials(name),
      alt: name,
      sx: {
        width: 36,
        height: 36,
        fontSize: 14,
        bgcolor: bg,
        color: theme.palette.getContrastText ? theme.palette.getContrastText(bg) : "#fff",
        border: avatarRoleBorder,
        boxSizing: "border-box",
      },
      title: name,
    };
  }, [userData, theme.palette, avatarRoleBorder]);

  const { data: permData, isLoading: permsLoading } = useEffectivePermissions();
  const isMasterUser = Number(userData?.master) === 1;
  const scopeSet = scopeSetFromPermissions(permData, { ...userData, master: isMasterUser ? 1 : 0 });
  const canViewBilling = can(scopeSet, "billing", "view");
  const canViewProfile = can(scopeSet, "profile", "view");
  const canViewTimeClock = can(scopeSet, "time_clock", "view");
  const isPartnerUser = userData?.scope === "partner";
  const onPlatformConsole = isPlatformConsolePath(location.pathname);
  const { loading: consoleAclLoading, can: canConsoleSection } = usePlatformConsoleAcl();
  const canViewInternalAsk =
    userData?.scope === "platform" &&
    !consoleAclLoading &&
    canConsoleSection(PLATFORM_CONSOLE_SEGMENT.internalAsk, "view");
  const onPartnerConsole = isPartnerConsolePath(location.pathname);
  const partnerPortalSession =
    isPartnerUser && (onPartnerConsole || hideWorkspaceChrome || !hasActiveTenantWorkspaceContext());
  const platformConsoleSession =
    onPlatformConsole ||
    (userData?.scope === "platform" && (hideWorkspaceChrome || !hasActiveTenantWorkspaceContext()));
  // Console master / portal representante: sem itens de app do tenant no menu do perfil.
  const portalSessionChrome =
    hideWorkspaceChrome || onPlatformConsole || onPartnerConsole || partnerPortalSession || platformConsoleSession;
  // IA do tenant não roda no console master; no portal do representante usa /billing/partner/assistant.
  const showPlatformAiButton =
    (platformAi?.canOpenPlatformAi ?? true) &&
    (partnerPortalSession || (!platformConsoleSession && !onPlatformConsole));

  const avatarMenuComputed = useMemo(() => {
    const menuBorder = sessionRole ? `3px solid ${sessionRole.accent}` : undefined;
    // versão levemente maior para o header do menu
    const name = userData?.nome || userData?.name || userData?.email || "Usuário";
    const url = getUserAvatarUrl(userData);
    if (url) {
      return {
        src: url,
        alt: name,
        sx: {
          width: 40,
          height: 40,
          ...(menuBorder ? { border: menuBorder, boxSizing: "border-box" } : {}),
        },
      };
    }
    const bg = stringToColor(name);
    return {
      children: getInitials(name),
      alt: name,
      sx: {
        width: 40,
        height: 40,
        fontSize: 16,
        bgcolor: bg,
        color: theme.palette.getContrastText ? theme.palette.getContrastText(bg) : "#fff",
        ...(menuBorder ? { border: menuBorder, boxSizing: "border-box" } : {}),
      },
    };
  }, [userData, theme.palette, sessionRole]);

  // console.log('topoBar', status);

  const workspaceTenantKey = useMemo(() => {
    // Nunca preferir localStorage.company global — ele é compartilhado entre abas.
    const fromUrl = readWorkspaceSlugFromUrl(location.search);
    const fromPin = getActiveWorkspaceSlug();
    return String((fromUrl || fromPin || companyKey || "").trim());
  }, [companyKey, location.pathname, location.search, location.key]);

  const workspaceLabel = workspaceTenantKey.length ? workspaceTenantKey : null;

  return (
    <>
      <Tour
        key={location.pathname}
        steps={tourSteps}
        isTourActive={isTourActive && tourReady}
        onTourEnd={() => setIsTourActive(false)}
      />

      <AppBar
        position="fixed"
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
          zIndex: 1201,
        }}
      >
        {sessionRole ? <SessionRoleWorkspaceSwitcher sessionRole={sessionRole} /> : null}
        {isModelWorkspace && !hideWorkspaceChrome ? (
          <Box sx={{ px: isMobile ? 1.25 : 2.5, pb: 0.75 }}>
            <Chip
              size="small"
              color="secondary"
              variant="outlined"
              label="Workspace modelo — não é um cliente"
            />
          </Box>
        ) : null}
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: isMobile ? "0 10px" : "0 20px",
            minHeight: sessionRole ? 56 : 64,
          }}
        >
          {/* Esquerda: menu + logo */}
          <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
            {/* Botão de Colapso */}
            {!hideWorkspaceChrome && (
              <IconButton
                onClick={() => (isMobile ? toggleDrawer() : setCollapsed(!collapsed))}
                className="tour-menu"
                sx={{
                  padding: 0,
                  paddingRight: isMobile ? 2 : 3,
                  color: theme.palette.text.primary,
                  flex: "0 0 auto",
                }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Logo */}
            <Box
              className="tour-logo"
              sx={{
                display: "flex",
                alignItems: "center",
                width: isMobile ? "120px" : "150px",
                cursor: "pointer",
                flex: "0 0 auto",
              }}
              onClick={() => navigate("/")}
            >
              <img
                src={theme.palette.mode === "dark" ? brand.assets.logo.dark : brand.assets.logo.light}
                alt={`${brand.name} Logo`}
                style={{ height: 40 }}
              />
            </Box>
          </Box>

          {/* Centro: busca (desktop) / atalho (mobile) */}
          <Box sx={{ display: "flex", alignItems: "center", flex: "1 1 auto", minWidth: 0 }}>
            {hideWorkspaceChrome ? null : isMobile ? (
              <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                <Tooltip title={t("topbar.searchCompanyTooltip")}>
                  <IconButton
                    className="tour-mobile-search-company"
                    onClick={() => setModalOpen(true)}
                    sx={{ color: theme.palette.text.primary }}
                  >
                    <Search />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Box
                className="tour-search"
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginLeft: "10px",
                  minWidth: 0,
                }}
              >
                <SearchAutocomplete />
              </Box>
            )}
          </Box>

          {/* Direita: seletor de empresas + ícones */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flex: "0 0 auto", minWidth: 0 }}>
            {!hideWorkspaceChrome && !isMobile && (
              <Box
                className="tour-company"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  marginLeft: "16px",
                  flex: "0 0 auto",
                }}
              >
                <Box
                  sx={{
                    width: "250px",
                    maxWidth: "250px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <CompanySelector
                    key={workspaceTenantKey || "no-workspace"}
                    workspaceLabel={!isMobile ? workspaceLabel : null}
                    workspaceTenantKey={workspaceTenantKey}
                  />
                </Box>
              </Box>
            )}

            {onPlatformConsole && canViewInternalAsk ? (
              <Tooltip title={t("topbar.internalAsk")}>
                <IconButton
                  onClick={() => navigate(PLATFORM_CONSOLE_PATH.internalAsk)}
                  aria-label={t("topbar.internalAsk")}
                  sx={{
                    marginLeft: "8px",
                    color: location.pathname.includes("/internal-ask")
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                  }}
                >
                  <PsychologyOutlined />
                </IconButton>
              </Tooltip>
            ) : null}

            <NotificationBell />

          {/* Botão de troca de tema */}
          <Tooltip title="Alterar Tema">
            <IconButton
              className="tour-theme"
              onClick={handleThemeMenuOpen}
              sx={{ marginLeft: "16px", color: theme.palette.text.primary }}
            >
              {theme.palette.mode === "dark" ? <Brightness7 /> : <Brightness4 />}
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={themeAnchorEl}
            open={isThemeMenuOpen}
            onClose={handleThemeMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            {themeMenuOptions.map((themeOption) => (
              <MenuItem key={themeOption.value} onClick={() => handleThemeChange(themeOption.value)}>
                {themeOption.label}
              </MenuItem>
            ))}
          </Menu>

          {showPlatformAiButton ? (
            <Tooltip
              title={t("topbar.platformAiShortcut", {
                defaultValue: "Assistente da plataforma (Ctrl+I)",
              })}
            >
              <IconButton
                onClick={() => platformAi?.openPlatformAi?.()}
                className="tour-ai"
                sx={{ marginLeft: "8px", color: theme.palette.text.primary }}
              >
                <AutoAwesome />
              </IconButton>
            </Tooltip>
          ) : null}

          {/* Seletor de Status */}
          {/* <StatusSelector
            selectedStatus={status} 
            onStatusChange={handleStatusChange}
          /> */}

          {/* Perfil do usuário — contorno na cor da sessão (master / representante) */}
          <Box className="tour-profile" sx={{ display: "flex", alignItems: "center", marginLeft: "8px" }}>
            {sessionRole ? (
              <Tooltip title={sessionRole.tooltip}>
                <IconButton onClick={handleProfileMenuOpen} sx={{ p: 0 }} aria-label={sessionRole.tooltip}>
                  <Avatar {...avatarComputed} />
                </IconButton>
              </Tooltip>
            ) : (
              <IconButton onClick={handleProfileMenuOpen} sx={{ p: 0 }}>
                <Avatar {...avatarComputed} />
              </IconButton>
            )}
          </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Menu do perfil */}
      <Menu
        anchorEl={profileAnchorEl}
        open={isProfileMenuOpen}
        onClose={handleProfileMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          elevation: 3,
          sx: {
            width: 260,
            padding: 2,
            borderRadius: 3,
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        {/* Cabeçalho do Menu */}
        <Box display="flex" alignItems="center" gap={2} padding={1.5} borderBottom={`1px solid ${theme.palette.divider}`}>
          <Avatar {...avatarMenuComputed} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              noWrap
              sx={{
                maxWidth: "180px",
              }}
            >
              {userData?.nome}
            </Typography>
            <Tooltip title={userData?.email || ""}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  maxWidth: "180px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  wordBreak: "break-all",
                }}
              >
                {userData?.email}
              </Typography>
            </Tooltip>
          </Box>
        </Box>

        {/* Opções do Menu — no console master/partner só o que faz sentido fora do tenant */}
        {!portalSessionChrome && canViewProfile ? (
          <MenuItem
            onClick={() => {
              handleProfileMenuClose();
              navigate("/profile");
            }}
            sx={{ mt: 1 }}
          >
            <ListItemIcon>
              <Person fontSize="small" />
            </ListItemIcon>
            <Typography>{t("topbar.myProfile")}</Typography>
          </MenuItem>
        ) : null}
        {!portalSessionChrome && !permsLoading && canViewTimeClock ? (
          <MenuItem
            onClick={() => {
              handleProfileMenuClose();
              navigate("/meu-ponto");
            }}
            sx={{ mt: 0.5 }}
          >
            <ListItemIcon>
              <AccessTimeOutlined fontSize="small" />
            </ListItemIcon>
            <Typography>{t("topbar.myTimeClock")}</Typography>
          </MenuItem>
        ) : null}
        {!portalSessionChrome ? (
          <MenuItem
            onClick={() => {
              handleProfileMenuClose();
              openTeamTasksPopupWindow({ inbox: true });
            }}
            sx={{ mt: 0.5 }}
          >
            <ListItemIcon>
              <AssignmentOutlined fontSize="small" />
            </ListItemIcon>
            <Typography>{t("topbar.myTasks")}</Typography>
          </MenuItem>
        ) : null}
        {!portalSessionChrome ? (
          <MenuItem
            onClick={() => {
              handleProfileMenuClose();
              openTeamPopupWindow(undefined, { section: "atendimento" });
            }}
            sx={{ mt: 0.5 }}
          >
            <ListItemIcon>
              <SpaceDashboardOutlined fontSize="small" />
            </ListItemIcon>
            <Typography>{t("topbar.cockpit")}</Typography>
          </MenuItem>
        ) : null}
        <MenuItem
          onClick={() => {
            handleProfileMenuClose();
            if (portalSessionChrome) {
              // Treinamentos do hub são do tenant; no console use a Central de Ajuda global.
              navigate("/help");
            } else {
              openHelpOnboarding();
            }
          }}
          sx={{ mt: portalSessionChrome ? 1 : 0.5 }}
        >
          <ListItemIcon>
            <HelpOutline fontSize="small" />
          </ListItemIcon>
          <Typography>
            {portalSessionChrome ? t("topbar.helpCenter", { defaultValue: "Central de Ajuda" }) : t("topbar.helpOnboarding")}
          </Typography>
        </MenuItem>
        {!portalSessionChrome && !permsLoading && canViewBilling ? (
          <MenuItem
            onClick={() => {
              handleProfileMenuClose();
              navigate("/settings/billing");
            }}
            sx={{ mt: 0.5 }}
          >
            <ListItemIcon>
              <ReceiptLongOutlined fontSize="small" />
            </ListItemIcon>
            <Typography>{t("topbar.billingWorkspace")}</Typography>
          </MenuItem>
        ) : null}
        {isPartnerUser && !onPartnerConsole ? (
          <MenuItem
            onClick={() => {
              handleProfileMenuClose();
              navigate(PARTNER_CONSOLE_PATH.overview);
            }}
            sx={{ mt: 0.5 }}
          >
            <ListItemIcon>
              <Storefront fontSize="small" />
            </ListItemIcon>
            <Typography>{t("topbar.partnerResellerPanel")}</Typography>
          </MenuItem>
        ) : null}
        {!permsLoading && isMasterUser && !onPlatformConsole ? (
          <MenuItem
            onClick={() => {
              handleProfileMenuClose();
              navigate("/platform/xprobase");
            }}
            sx={{ mt: 0.5 }}
          >
            <ListItemIcon>
              <AdminPanelSettings fontSize="small" />
            </ListItemIcon>
            <Typography>{t("topbar.platformXprobase")}</Typography>
          </MenuItem>
        ) : null}
        <MenuItem onClick={logout} sx={{ mt: 1 }}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <Typography>{t("topbar.logout")}</Typography>
        </MenuItem>
      </Menu>

      {/* Modal de busca e seleção de empresa para mobile */}
      {!hideWorkspaceChrome && (
        <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{t("topbar.searchCompanyDialogTitle")}</DialogTitle>
          <DialogContent>
            <Box sx={{ marginBottom: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t("topbar.searchClients")}
              </Typography>
              <SearchAutocomplete />
            </Box>
            <Box>
              <Typography variant="h6" gutterBottom>
                {t("topbar.selectCompany")}
              </Typography>
              <CompanySelector
                key={workspaceTenantKey || "no-workspace"}
                workspaceTenantKey={workspaceTenantKey}
              />
            </Box>
            <Box display="flex" justifyContent="flex-end" mt={3}>
              <Button variant="contained" onClick={() => setModalOpen(false)}>
                {t("close")}
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      )}

      <HelpOnboardingDialog
        open={helpOnboardingOpen}
        onClose={() => setHelpOnboardingOpen(false)}
        onStartTour={() => {
          setHelpOnboardingOpen(false);
          setIsTourActive(true);
        }}
      />
    </>
  );
};

export default TopBar;
